import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { FirmCombobox } from '@/components/firms/FirmCombobox';
import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { cmsImportsApiClient } from '@/services/api/cmsImportsService';
import { selectUser } from '@/state/selectors/root-selectors';

// CMS type options that support API sync
const CMS_SYNC_OPTIONS = [
  { value: 'casepeer', label: 'CasePeer' },
  { value: 'clio', label: 'Clio' },
  { value: 'filevine', label: 'Filevine' },
  { value: 'mycase', label: 'MyCase' },
  { value: 'neos', label: 'Neos' },
  { value: 'litify', label: 'Litify' },
] as const;

type CmsSyncType = (typeof CMS_SYNC_OPTIONS)[number]['value'];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  defaultFirmId?: string;
}

export const CMSSyncDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  defaultFirmId,
}) => {
  const currentUser = useSelector(selectUser);
  const [triggerSync, { isLoading, error }] =
    cmsImportsApiClient.useCmsImportSyncFromApiMutation();

  // Staff users can select any firm; firm users are locked to their own firm
  const isStaff = currentUser?.is_staff ?? false;
  const userFirmId = currentUser?.firm;

  const [firmId, setFirmId] = useState<string>(
    userFirmId ?? defaultFirmId ?? '',
  );
  const [cmsType, setCmsType] = useState<CmsSyncType>('filevine');

  const selectedCmsLabel =
    CMS_SYNC_OPTIONS.find((opt) => opt.value === cmsType)?.label || 'CMS';

  const handleSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();
    if (!firmId) return;

    try {
      const result = await triggerSync({
        body: {
          firm_id: firmId,
          cms_type: cmsType,
        },
      }).unwrap();
      toast.success(result.message ?? 'CMS sync started successfully');
      onClose();
    } catch (err) {
      console.error('Sync failed:', err);
      toast.error('Failed to start CMS sync');
    }
  };

  const getErrorMessage = () => {
    if (!error) return null;
    if ('data' in error && error.data) {
      if (typeof error.data === 'object' && 'detail' in error.data) {
        return String(error.data.detail);
      }
      return JSON.stringify(error.data);
    }
    if ('status' in error) {
      return `Error ${error.status}`;
    }
    return 'An unexpected error occurred';
  };

  return (
    <Dialog open={isOpen} onClose={onClose} size="lg">
      <form onSubmit={handleSubmit}>
        <DialogBody>
          <DialogTitle>Sync Tasks from CMS API</DialogTitle>

          {error && (
            <div className="mt-4 rounded-md bg-red-50 dark:bg-red-900/20 p-4">
              <p className="text-sm text-red-800 dark:text-red-200">
                <strong>Sync failed:</strong> {getErrorMessage()}
              </p>
            </div>
          )}

          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
            Connect directly to your CMS API to sync tasks into Finch. The firm
            must have a configured {selectedCmsLabel} integration.
          </p>

          <div className="mt-6 space-y-5">
            {/* CMS Type Selector */}
            <div>
              <div className="block text-sm font-medium mb-1.5 text-zinc-900 dark:text-white">
                CMS Type
              </div>
              <div className="relative">
                <select
                  value={cmsType}
                  onChange={(e) => setCmsType(e.target.value as CmsSyncType)}
                  className="w-full appearance-none rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 pr-10 text-sm text-zinc-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {CMS_SYNC_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <ChevronUpDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
              </div>
            </div>

            {/* Firm Selector - only shown to staff */}
            {isStaff && (
              <div>
                <div className="block text-sm font-medium mb-1.5 text-zinc-900 dark:text-white">
                  Select Firm
                </div>
                <FirmCombobox
                  value={firmId}
                  onChange={setFirmId}
                  className="w-full"
                  panelClassName="z-50"
                />
                <p className="mt-1.5 text-xs text-zinc-500 dark:text-zinc-400">
                  The selected firm must have a {selectedCmsLabel} integration
                  enabled.
                </p>
              </div>
            )}
          </div>

          <DialogActions>
            <Button color="zinc" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button color="blue" type="submit" disabled={!firmId || isLoading}>
              {isLoading ? 'Starting Sync...' : `Sync from ${selectedCmsLabel}`}
            </Button>
          </DialogActions>
        </DialogBody>
      </form>
    </Dialog>
  );
};
